import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle, Barcode, Check, Clipboard, CreditCard,
  Download, ExternalLink, Eye, FileText, Globe, ImagePlus,
  Loader2, Mail, MapPin, Package, Phone, QrCode,
  ReceiptText, Search, ScanLine, Share2, Tag, Type,
  WifiOff, X, Zap, ZapOff,
} from "lucide-react";
import {
  captureFrame, getVideoTrack, readTrackSupport,
  setTorch, setZoom, startCamera, stopStream, vibrate,
} from "../lib/camera";
import { applyFilterToBlob, getFilterById } from "../lib/filters";
import { blobToDataUrl, exportCsv, exportPdfFromImage, shareBlob } from "../lib/exportUtils";
import { requestGroqAnalysis } from "../lib/aiClient";
import { decodeCodeFromBlob, scannerPromptForMode, SCAN_MODES } from "../lib/scannerUtils";
import { getAll, saveScan } from "../lib/indexedDb";

// ─── tiny helpers ────────────────────────────────────────────────────────────

function tryParseJson(text) {
  if (!text) return null;
  const cleaned = text
    .replace(/^```(?:json)?\s*/m, "")
    .replace(/\s*```\s*$/m, "")
    .trim();
  try { return JSON.parse(cleaned); } catch (_) {}
  const m = cleaned.match(/\{[\s\S]*\}/);
  if (m) try { return JSON.parse(m[0]); } catch (_) {}
  return null;
}

const isUrl   = (s) => /^https?:\/\//i.test(String(s ?? "").trim());
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s ?? "").trim());
const isPhone = (s) => /^[\+]?[\d\s\-\(\)\.]{7,}$/.test(String(s ?? "").trim());

function formatTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso);
  if (diff < 60_000)     return "just now";
  if (diff < 3_600_000)  return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ─── mode icon map ───────────────────────────────────────────────────────────

function ModeIcon({ id, size = 18 }) {
  const MAP = {
    qr:             QrCode,
    barcode:        Barcode,
    document:       FileText,
    receipt:        ReceiptText,
    "business-card": CreditCard,
    product:        Package,
    label:          Tag,
    text:           Type,
    object:         Eye,
  };
  const Icon = MAP[id] ?? ScanLine;
  return <Icon size={size} />;
}

// ─── structured result renderers ─────────────────────────────────────────────

function QrResult({ value }) {
  const url   = isUrl(value);
  const email = !url && isEmail(value);
  const phone = !url && !email && isPhone(value);
  return (
    <div className="sc-qr-result">
      <pre className="sc-qr-value">{value}</pre>
      <div className="sc-qr-links">
        {url   && <a href={value} target="_blank" rel="noopener noreferrer" className="sc-link-btn"><ExternalLink size={14} /> Open Link</a>}
        {email && <a href={`mailto:${value}`} className="sc-link-btn"><Mail size={14} /> Send Email</a>}
        {phone && <a href={`tel:${value.replace(/\s/g, "")}`} className="sc-link-btn"><Phone size={14} /> Call</a>}
      </div>
    </div>
  );
}

function ReceiptResult({ data, raw }) {
  if (!data?.merchant) return <div className="sc-text-result">{raw}</div>;
  const items = Array.isArray(data.items) ? data.items : [];
  return (
    <div className="sc-receipt">
      <p className="sc-receipt-merchant">{data.merchant}</p>
      <div className="sc-receipt-meta">
        {data.date     && <span>{data.date}</span>}
        {data.category && <span className="sc-tag">{data.category}</span>}
      </div>
      {items.length > 0 && (
        <div className="sc-receipt-items">
          {items.map((item, i) => (
            <div key={i} className="sc-line-row">
              <span>{item.name ?? item}</span>
              <span className="sc-muted">{item.price ?? ""}</span>
            </div>
          ))}
        </div>
      )}
      <div className="sc-receipt-total">
        <span>Total</span>
        <span className="sc-total-val">{data.total ?? "—"}</span>
      </div>
      {data.tax     && <p className="sc-muted" style={{ margin: "4px 12px" }}>Tax: {data.tax}</p>}
      {data.summary && <p className="sc-summary">{data.summary}</p>}
    </div>
  );
}

function ContactResult({ data, raw }) {
  if (!data?.name) return <div className="sc-text-result">{raw}</div>;
  return (
    <div className="sc-contact">
      <p className="sc-contact-name">{data.name}</p>
      {data.title   && <p className="sc-contact-title">{data.title}</p>}
      {data.company && <p className="sc-contact-company">{data.company}</p>}
      <div className="sc-contact-rows">
        {data.phone   && <a href={`tel:${data.phone.replace(/\s/g, "")}`}            className="sc-contact-row"><Phone   size={15} /><span>{data.phone}</span></a>}
        {data.email   && <a href={`mailto:${data.email}`}                             className="sc-contact-row"><Mail    size={15} /><span>{data.email}</span></a>}
        {data.website && <a href={data.website} target="_blank" rel="noopener noreferrer" className="sc-contact-row"><Globe   size={15} /><span>{data.website}</span></a>}
        {data.address && <div className="sc-contact-row"><MapPin  size={15} /><span>{data.address}</span></div>}
        {data.notes   && <div className="sc-contact-row"><FileText size={15} /><span>{data.notes}</span></div>}
      </div>
    </div>
  );
}

function DocSection({ label, items, variant }) {
  const filtered = (items ?? []).filter(Boolean);
  if (!filtered.length) return null;
  return (
    <div className="sc-doc-section">
      <p className={`sc-section-label ${variant ?? ""}`}>{label}</p>
      <ul className={`sc-doc-list ${variant ?? ""}`}>
        {filtered.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    </div>
  );
}

function DocumentResult({ data, raw }) {
  if (!data?.summary) return <div className="sc-text-result">{raw}</div>;
  return (
    <div className="sc-document">
      {data.title   && <p className="sc-doc-title">{data.title}</p>}
      <p className="sc-doc-summary">{data.summary}</p>
      <DocSection label="Key Points"   items={data.keyPoints}   />
      <DocSection label="Action Items" items={data.actionItems} variant="accent" />
      <DocSection label="Warnings"     items={data.warnings}    variant="warn"   />
    </div>
  );
}

function ProductResult({ data, raw }) {
  if (!data?.productName) return <div className="sc-text-result">{raw}</div>;
  return (
    <div className="sc-document">
      <p className="sc-doc-title">
        {data.productName}{data.brand ? ` · ${data.brand}` : ""}
      </p>
      {data.category    && <span className="sc-tag" style={{ marginBottom: 8, display: "inline-flex" }}>{data.category}</span>}
      {data.description && <p className="sc-doc-summary">{data.description}</p>}
      <DocSection label="Key Information" items={data.keyInfo}   />
      <DocSection label="Warnings"        items={data.warnings}  variant="warn" />
      {data.notes && <p className="sc-summary">{data.notes}</p>}
    </div>
  );
}

function ScanResult({ result }) {
  const data = tryParseJson(result.text);
  switch (result.type) {
    case "qr":
    case "barcode":
      return <QrResult value={result.text} />;
    case "receipt":
      return <ReceiptResult data={data} raw={result.text} />;
    case "business-card":
      return <ContactResult data={data} raw={result.text} />;
    case "document":
      return <DocumentResult data={data} raw={result.text} />;
    case "product":
    case "label":
      return <ProductResult data={data} raw={result.text} />;
    default:
      return <div className="sc-text-result">{result.text}</div>;
  }
}

// ─── main component ──────────────────────────────────────────────────────────

export default function ScannerView({ online, settings }) {
  const videoRef      = useRef(null);
  const streamRef     = useRef(null);
  const uploadRef     = useRef(null);
  const autoTimerRef  = useRef(null);

  const [mode,           setMode]           = useState(() =>
    SCAN_MODES.find((m) => m.id === settings?.defaultScanMode) || SCAN_MODES[0]
  );
  const [busy,           setBusy]           = useState(false);
  const [result,         setResult]         = useState(null);
  const [resultOpen,     setResultOpen]     = useState(false);
  const [error,          setError]          = useState("");
  const [torchOn,        setTorchOn]        = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [zoomLevel,      setZoomLevel]      = useState(1);
  const [zoomSupported,  setZoomSupported]  = useState(false);
  const [autoScan,       setAutoScan]       = useState(Boolean(settings?.autoScan));
  const [history,        setHistory]        = useState([]);
  const [scanFlash,      setScanFlash]      = useState(false);
  const [copied,         setCopied]         = useState(false);

  // ── camera init ────────────────────────────────────────────────────────────

  useEffect(() => {
    let alive = true;
    startCamera({ facingMode: "environment" })
      .then(async (stream) => {
        if (!alive) return stopStream(stream);
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        const track   = getVideoTrack(stream);
        const support = readTrackSupport(track);
        setTorchSupported(Boolean(support.torch));
        setZoomSupported(Boolean(support.zoom));
      })
      .catch(() => setError("Camera unavailable. Tap the gallery button to scan from a photo."));
    return () => {
      alive = false;
      stopStream(streamRef.current);
    };
  }, []);

  // ── load scan history ──────────────────────────────────────────────────────

  useEffect(() => {
    getAll("scans")
      .then((all) =>
        setHistory(
          [...all].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 24)
        )
      )
      .catch(() => {});
  }, []);

  // ── auto-scan (QR / barcode only) ─────────────────────────────────────────

  useEffect(() => {
    const stop = () => {
      if (autoTimerRef.current) { clearInterval(autoTimerRef.current); autoTimerRef.current = null; }
    };
    if (!autoScan || mode.kind !== "local" || resultOpen) { stop(); return; }

    autoTimerRef.current = setInterval(async () => {
      if (busy || resultOpen || !videoRef.current) return;
      try {
        const blob = await captureFrame(videoRef.current);
        const text = await decodeCodeFromBlob(blob);
        if (!text) return;
        stop();
        vibrate([50, 40, 50]);
        setScanFlash(true);
        setTimeout(() => setScanFlash(false), 260);
        const saved = await saveScan({ type: mode.id, title: mode.label, text, blob });
        setResult(saved);
        setResultOpen(true);
        setAutoScan(false);
        setHistory((p) => [saved, ...p.filter((h) => h.id !== saved.id)].slice(0, 24));
      } catch (_) {}
    }, 800);

    return stop;
  }, [autoScan, mode, resultOpen, busy]);

  // ── core scan ──────────────────────────────────────────────────────────────

  const scan = useCallback(async (blobInput) => {
    setBusy(true);
    setError("");
    setScanFlash(true);
    setTimeout(() => setScanFlash(false), 260);
    vibrate(40);

    try {
      const raw = blobInput ?? await captureFrame(videoRef.current);
      let text = "";

      if (mode.kind === "local") {
        text = await decodeCodeFromBlob(raw);
        if (!text)
          text = "No QR code or barcode detected. Try better lighting, move closer, or ensure the code is fully in frame.";

      } else if (online) {
        const needsFilter = mode.id === "document" || mode.id === "receipt";
        const blob = needsFilter
          ? await applyFilterToBlob(raw, getFilterById("document-sharp"), { strength: 80 })
          : raw;
        const dataUrl = await blobToDataUrl(blob);
        const ai = await requestGroqAnalysis({
          action:       mode.label,
          prompt:       scannerPromptForMode(mode.id),
          imageDataUrl: dataUrl,
        });
        text = ai.output ?? "";

      } else {
        text = "Offline — scan saved locally. AI interpretation requires an internet connection.";
      }

      const saved = await saveScan({ type: mode.id, title: mode.label, text, blob: raw });
      setResult(saved);
      setResultOpen(true);
      setHistory((p) => [saved, ...p.filter((h) => h.id !== saved.id)].slice(0, 24));
    } catch (err) {
      setError(err.message ?? "Scan failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [mode, online]);

  // ── gallery upload ─────────────────────────────────────────────────────────

  const handleUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    scan(new Blob([file], { type: file.type }));
  }, [scan]);

  // ── torch / zoom ───────────────────────────────────────────────────────────

  const toggleTorch = useCallback(async () => {
    if (!torchSupported) return;
    const next = !torchOn;
    const ok = await setTorch(streamRef.current, next);
    if (ok) setTorchOn(next);
  }, [torchOn, torchSupported]);

  const changeZoom = useCallback(async (level) => {
    if (!zoomSupported) return;
    const actual = await setZoom(streamRef.current, level);
    if (actual !== false) setZoomLevel(level);
  }, [zoomSupported]);

  // ── auto-scan toggle ───────────────────────────────────────────────────────

  const toggleAutoScan = useCallback(() => {
    if (mode.kind !== "local") return;
    setAutoScan((v) => !v);
  }, [mode]);

  // ── export actions ─────────────────────────────────────────────────────────

  const handleCopy = useCallback(() => {
    if (!result?.text) return;
    navigator.clipboard?.writeText(result.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {});
  }, [result]);

  const handleShare = useCallback(async () => {
    if (!result) return;
    try {
      if (result.blob) {
        const ok = await shareBlob(result.blob, result.title ?? "Scan");
        if (!ok) await navigator.share?.({ title: result.title, text: result.text });
      } else {
        await navigator.share?.({ title: result.title, text: result.text });
      }
    } catch (_) {}
  }, [result]);

  const handlePdf = useCallback(() => {
    if (result?.blob) exportPdfFromImage(result.blob, `lenspro-scan-${result.type}.pdf`);
  }, [result]);

  const handleCsv = useCallback(() => {
    if (!result) return;
    exportCsv(
      [{ mode: result.type, title: result.title, text: result.text, createdAt: result.createdAt }],
      "lenspro-scan.csv"
    );
  }, [result]);

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <section className="scanner-page">

      {/* ── camera feed ─────────────────────────────────────────────────── */}
      <video ref={videoRef} className="scanner-video" playsInline muted autoPlay />

      {/* ── capture flash ───────────────────────────────────────────────── */}
      <div className={`sc-flash${scanFlash ? " on" : ""}`} aria-hidden="true" />

      {/* ── top bar ─────────────────────────────────────────────────────── */}
      <header className="sc-top safe-top">
        <button
          className={`sc-icon-btn${torchOn ? " on" : ""}`}
          onClick={toggleTorch}
          disabled={!torchSupported}
          aria-label={torchOn ? "Torch off" : "Torch on"}
          title={torchSupported ? (torchOn ? "Torch off" : "Torch on") : "Torch not supported on this device"}
        >
          {torchOn ? <Zap size={18} /> : <ZapOff size={18} />}
        </button>

        <div className="sc-brand">
          <ScanLine size={17} />
          Scanner
        </div>

        <div className="sc-top-right">
          {online
            ? <span className="sc-online-dot" title="Online — AI ready" />
            : <WifiOff size={14} className="sc-offline-icon" title="Offline" />
          }
          <span className="sc-net-label">{online ? "Online" : "Offline"}</span>
        </div>
      </header>

      {/* ── viewfinder ──────────────────────────────────────────────────── */}
      <div className="sc-finder-wrap" aria-hidden="true">
        <div className={`sc-finder${busy ? " scanning" : ""}${autoScan && mode.kind === "local" ? " auto" : ""}`}>
          <div className="sc-corner sc-tl" />
          <div className="sc-corner sc-tr" />
          <div className="sc-corner sc-bl" />
          <div className="sc-corner sc-br" />
          <div className="sc-laser" />
        </div>

        {/* zoom pills float below finder */}
        {zoomSupported && (
          <div className="sc-zoom-row">
            {[1, 1.5, 2].map((z) => (
              <button
                key={z}
                className={`sc-zoom-pill${zoomLevel === z ? " on" : ""}`}
                onClick={() => changeZoom(z)}
              >
                {z}×
              </button>
            ))}
          </div>
        )}

        <p className="sc-hint">
          {autoScan && mode.kind === "local" ? "Auto-scanning…" : mode.hint}
        </p>
      </div>

      {/* ── bottom controls ─────────────────────────────────────────────── */}
      <div className="sc-bottom safe-bottom">

        {/* mode strip */}
        <div className="sc-mode-strip" role="tablist" aria-label="Scan mode">
          {SCAN_MODES.map((m) => (
            <button
              key={m.id}
              role="tab"
              aria-selected={mode.id === m.id}
              className={`sc-mode-chip${mode.id === m.id ? " on" : ""}`}
              onClick={() => { setMode(m); setError(""); setAutoScan(false); }}
            >
              <ModeIcon id={m.id} size={13} />
              {m.label}
            </button>
          ))}
        </div>

        {/* shutter row */}
        <div className="sc-shutter-row">
          <button
            className="sc-side-btn"
            onClick={() => uploadRef.current?.click()}
            aria-label="Scan from gallery"
            title="Scan from gallery"
          >
            <ImagePlus size={21} />
          </button>

          <button
            className="sc-shutter"
            onClick={() => scan()}
            disabled={busy}
            aria-label={busy ? "Scanning…" : "Scan"}
          >
            {busy
              ? <Loader2 className="spin" size={26} />
              : <ScanLine size={26} />
            }
          </button>

          <button
            className={`sc-side-btn${autoScan ? " on" : ""}${mode.kind !== "local" ? " muted" : ""}`}
            onClick={toggleAutoScan}
            disabled={mode.kind !== "local"}
            aria-label={autoScan ? "Stop auto-scan" : "Auto-scan"}
            title={mode.kind !== "local" ? "Auto-scan works in QR / Barcode modes" : (autoScan ? "Stop auto-scan" : "Start auto-scan")}
          >
            <Search size={21} />
          </button>
        </div>
      </div>

      {/* ── gallery file input ───────────────────────────────────────────── */}
      <input
        ref={uploadRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleUpload}
      />

      {/* ── error toast ─────────────────────────────────────────────────── */}
      {error && (
        <div className="sc-toast" role="alert">
          <AlertCircle size={15} />
          <span className="sc-toast-msg">{error}</span>
          <button className="sc-toast-dismiss" onClick={() => setError("")} aria-label="Dismiss">
            <X size={13} />
          </button>
        </div>
      )}

      {/* ── result sheet ────────────────────────────────────────────────── */}
      {resultOpen && result && (
        <div
          className="sc-backdrop"
          onClick={() => setResultOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Scan result"
        >
          <div className="sc-sheet" onClick={(e) => e.stopPropagation()}>

            {/* handle */}
            <div className="sc-handle" />

            {/* header */}
            <div className="sc-sheet-header">
              <div className="sc-result-icon">
                <ModeIcon id={result.type} size={18} />
              </div>
              <div className="sc-result-meta">
                <span className="sc-result-label">{result.title}</span>
                <span className="sc-result-time">{formatTime(result.createdAt)}</span>
              </div>
              <button
                className="sc-close"
                onClick={() => setResultOpen(false)}
                aria-label="Close"
              >
                <X size={17} />
              </button>
            </div>

            {/* body */}
            <div className="sc-sheet-body">
              <ScanResult result={result} />
            </div>

            {/* actions */}
            <div className="sc-sheet-actions">
              <button className={copied ? "sc-action-btn on" : "sc-action-btn"} onClick={handleCopy}>
                {copied ? <Check size={15} /> : <Clipboard size={15} />}
                {copied ? "Copied!" : "Copy"}
              </button>
              <button className="sc-action-btn" onClick={handleShare}>
                <Share2 size={15} /> Share
              </button>
              {result.blob && (
                <button className="sc-action-btn" onClick={handlePdf}>
                  <Download size={15} /> PDF
                </button>
              )}
              <button className="sc-action-btn" onClick={handleCsv}>
                <FileText size={15} /> CSV
              </button>
            </div>

            {/* recent scans history */}
            {history.length > 1 && (
              <div className="sc-history">
                <p className="sc-history-title">Recent Scans</p>
                <div className="sc-history-strip">
                  {history.slice(0, 12).map((h) => (
                    <button
                      key={h.id}
                      className={`sc-history-card${result?.id === h.id ? " on" : ""}`}
                      onClick={() => setResult(h)}
                    >
                      <span className="sc-history-icon"><ModeIcon id={h.type} size={12} /></span>
                      <span className="sc-history-label">{h.title}</span>
                      <span className="sc-history-time">{formatTime(h.createdAt)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </section>
  );
}
